/**
 * @file 本を開いたときの「前回開いていたページ」の決め方と、端末に覚えるページ。
 *
 * @what: まなぶ人の閲覧位置 (サーバ、端末をまたいで共有する) と、端末に覚えたページ
 *   (localStorage、送れなかったときの手がかり) の新しい方を「前回開いていたページ」にする。
 *   あわせて、資料ごとの前回の学習の記録 (lastSourceLogs) を引く。
 * @why: どの本も、どの端末で開いても、前回開いていたページから始める (代表の判断、2026-09-14)。
 *   それまでは「前回の記録の次のページ」も候補にしていたが、記録を付けた日に開いていたページと
 *   違うページが開くので外した。
 * @failure: 純関数。localStorage が使えない端末では「覚えていない」として扱う。
 */

import type { StudyProgressLog } from "../api/plans";
import { parseSourceStudyLog } from "./sourceStudyLog";

/** 資料への前回の記録 (日付と、読み終えたページ)。 */
export interface LastSourceLog {
  date: string;
  toPage: number;
}

/** リーダーで最後に開いたページ (端末に覚えたもの)。 */
export interface LastOpenedPage {
  page: number;
  /** 開いた時刻 (epoch ms)。 */
  at: number;
}

/**
 * 記録の一覧から、資料ごとの前回の記録を引く。
 *
 * @what: detail が source_study で to_page を持つ記録のうち、日付が最も新しいもの。
 *   同じ日付なら一覧の先 (API の created_at 降順 = 後から残したもの) を採る。
 */
export function lastSourceLogs(logs: readonly StudyProgressLog[]): Map<string, LastSourceLog> {
  const out = new Map<string, LastSourceLog>();
  for (const log of logs) {
    if (!log.to_page) continue;
    const d = parseSourceStudyLog(log);
    if (!d) continue;
    const prev = out.get(d.source_id);
    if (!prev || log.date > prev.date)
      out.set(d.source_id, { date: log.date, toPage: log.to_page });
  }
  return out;
}

/**
 * 前回開いていたページを、閲覧位置 (サーバ) と端末に覚えたページの新しい方から決める。
 *
 * @what: どちらかしか無ければそれを使う。同じ時刻ならサーバの方を使う。ページ数が分かっていれば超えない。
 * @failure: どちらも無ければ null (資料の先頭から開く)。
 */
export function newerOpenedPage(
  server: LastOpenedPage | null | undefined,
  local: LastOpenedPage | null | undefined,
  pageCount?: number,
): number | null {
  const picked = server && (!local || server.at >= local.at) ? server : (local ?? null);
  if (picked === null) return null;
  return pageCount && picked.page > pageCount ? pageCount : picked.page;
}

/** localStorage のキーの前置き (家族 id と資料 id を後ろに付ける)。 */
export const LAST_OPENED_PAGE_KEY_PREFIX = "study.lastOpenedPage";

const openedKey = (familyId: string, sourceId: string) =>
  `${LAST_OPENED_PAGE_KEY_PREFIX}.${familyId}.${sourceId}`;

/** 端末に覚えた「最後に開いたページ」を読む。無い・壊れている・読めないときは null。 */
export function readLastOpenedPage(familyId: string, sourceId: string): LastOpenedPage | null {
  try {
    const raw = window.localStorage.getItem(openedKey(familyId, sourceId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const o = parsed as { page?: unknown; at?: unknown };
    if (!Number.isInteger(o.page) || (o.page as number) < 1) return null;
    if (typeof o.at !== "number" || !Number.isFinite(o.at)) return null;
    return { page: o.page as number, at: o.at };
  } catch {
    return null;
  }
}

/** 「最後に開いたページ」を端末に覚える。覚えられなくても読む操作には影響しない。 */
export function writeLastOpenedPage(
  familyId: string,
  sourceId: string,
  page: number,
  now: number = Date.now(),
): void {
  try {
    const value: LastOpenedPage = { page, at: now };
    window.localStorage.setItem(openedKey(familyId, sourceId), JSON.stringify(value));
  } catch {
    // 覚えられなくても読める
  }
}
